import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, PlayIcon, BookOpenIcon, TrendingUpIcon, ZapIcon, TargetIcon, UsersIcon, BuildingIcon, BrainIcon, DatabaseIcon, CheckCircleIcon, ClockIcon, MailIcon, LinkedinIcon, PhoneIcon, Edit2Icon, CopyIcon, ShareIcon, DownloadIcon } from 'lucide-react';
import { mockPlaysNew } from '../mockData';
export function PlaysDetailPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'workflow' | 'results'>('overview');
  const play = mockPlaysNew.find(p => p.id === id);
  if (!play) {
    return <div className="w-full h-screen flex items-center justify-center bg-gray-50" data-id="element-5879">
        <div className="text-center" data-id="element-5880">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5881">
            Play Not Found
          </h2>
          <p className="text-gray-600 mb-4" data-id="element-5882">
            The play you're looking for doesn't exist.
          </p>
          <button onClick={() => navigate('/plays/library')} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-5883">
            Back to Library
          </button>
        </div>
      </div>;
  }
  const getFeatureIcon = (feature: string) => {
    const icons: Record<string, React.ReactNode> = {
      Intent: <TrendingUpIcon className="w-5 h-5" data-id="element-5884" />,
      Enrichment: <DatabaseIcon className="w-5 h-5" data-id="element-5885" />,
      'Buying Groups': <UsersIcon className="w-5 h-5" data-id="element-5886" />,
      'Account Summaries': <TargetIcon className="w-5 h-5" data-id="element-5887" />,
      'Copilot Chat': <BrainIcon className="w-5 h-5" data-id="element-5888" />,
      Predictive: <BrainIcon className="w-5 h-5" data-id="element-5889" />,
      Technographics: <BuildingIcon className="w-5 h-5" data-id="element-5890" />,
      'Org Charts': <UsersIcon className="w-5 h-5" data-id="element-5891" />
    };
    return icons[feature] || <ZapIcon className="w-5 h-5" data-id="element-5892" />;
  };
  const getChannelIcon = (channel: string) => {
    const icons: Record<string, React.ReactNode> = {
      Email: <MailIcon className="w-4 h-4" data-id="element-5893" />,
      LinkedIn: <LinkedinIcon className="w-4 h-4" data-id="element-5894" />,
      Phone: <PhoneIcon className="w-4 h-4" data-id="element-5895" />
    };
    return icons[channel] || <ZapIcon className="w-4 h-4" data-id="element-5896" />;
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-5897">
      {/* Header */}
      <div className="bg-white border-b border-gray-200" data-id="element-5898">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4" data-id="element-5899">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4" data-id="element-5900">
            <button onClick={() => navigate('/plays/library')} className="hover:text-gray-900 transition-colors" data-id="element-5901">
              Plays Library
            </button>
            <span data-id="element-5902">/</span>
            <span className="text-gray-900 font-medium" data-id="element-5903">{play.title}</span>
          </div>
          {/* Title & Actions */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6" data-id="element-5904">
            <div className="flex-1" data-id="element-5905">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2" data-id="element-5906">
                {play.title}
              </h1>
              <p className="text-base lg:text-lg text-gray-600 mb-4" data-id="element-5907">
                {play.summary}
              </p>
              {/* Tags */}
              <div className="flex flex-wrap gap-2" data-id="element-5908">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg" data-id="element-5909">
                  {play.stage}
                </span>
                {play.motion.map(motion => <span key={motion} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg" data-id="element-5910">
                    {motion}
                  </span>)}
                {play.source === 'zoominfo' && <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-lg" data-id="element-5911">
                    ZoomInfo Recommended
                  </span>}
                <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg capitalize" data-id="element-5912">
                  {play.complexity}
                </span>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2" data-id="element-5913">
              <button onClick={() => navigate('/programs/overview')} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium" data-id="element-5914">
                <PlusIcon className="w-4 h-4" data-id="element-5915" />
                Add to Program
              </button>
              <button onClick={() => navigate('/gtm-studio/workspace')} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium" data-id="element-5916">
                <PlayIcon className="w-4 h-4" data-id="element-5917" />
                Launch in Studio
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5918">
                <CopyIcon className="w-4 h-4" data-id="element-5919" />
                <span className="hidden sm:inline" data-id="element-5920">Clone</span>
              </button>
              <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5921">
                <ShareIcon className="w-4 h-4" data-id="element-5922" />
                <span className="hidden sm:inline" data-id="element-5923">Share</span>
              </button>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-6 border-b border-gray-200" data-id="element-5924">
            {[{
            id: 'overview',
            label: 'Overview'
          }, {
            id: 'workflow',
            label: 'Workflow'
          }, {
            id: 'results',
            label: 'Expected Results'
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-5925">
                {tab.label}
              </button>)}
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-id="element-5926">
        {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-5927">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6" data-id="element-5928">
              {/* Trigger Logic */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5929">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5930">
                  Trigger Logic
                </h2>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-5931">
                  <p className="text-sm text-blue-900 font-medium mb-2" data-id="element-5932">
                    When to Activate:
                  </p>
                  <p className="text-sm text-blue-800" data-id="element-5933">
                    {play.trigger.logic_md}
                  </p>
                </div>
              </div>
              {/* Channel Mix */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5934">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5935">
                  Channel Mix
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-5936">
                  {play.channels.map(channel => <div key={channel} className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-5937">
                      <div className="w-10 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center text-gray-600" data-id="element-5938">
                        {getChannelIcon(channel)}
                      </div>
                      <span className="text-sm font-medium text-gray-900" data-id="element-5939">
                        {channel}
                      </span>
                    </div>)}
                </div>
              </div>
              {/* Target Personas */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5940">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5941">
                  Target Personas
                </h2>
                <div className="flex flex-wrap gap-2" data-id="element-5942">
                  {play.personas.map(persona => <span key={persona} className="px-4 py-2 bg-purple-50 text-purple-700 text-sm font-medium rounded-lg border border-purple-200" data-id="element-5943">
                      {persona}
                    </span>)}
                </div>
              </div>
              {/* Account Tiers */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5944">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5945">
                  Account Tiers
                </h2>
                <div className="flex flex-wrap gap-2" data-id="element-5946">
                  {play.account_tier.map(tier => <span key={tier} className="px-4 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-lg border border-green-200" data-id="element-5947">
                      {tier}
                    </span>)}
                </div>
              </div>
            </div>
            {/* Sidebar */}
            <div className="space-y-6" data-id="element-5948">
              {/* ZoomInfo Features */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5949">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5950">
                  ZoomInfo Features
                </h2>
                <div className="space-y-3" data-id="element-5951">
                  {play.zoominfo_features.map(feature => <div key={feature} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-5952">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center text-red-600" data-id="element-5953">
                        {getFeatureIcon(feature)}
                      </div>
                      <span className="text-sm font-medium text-gray-900" data-id="element-5954">
                        {feature}
                      </span>
                    </div>)}
                </div>
              </div>
              {/* Complexity & AI Readiness */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5955">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5956">
                  Implementation
                </h2>
                <div className="space-y-4" data-id="element-5957">
                  <div data-id="element-5958">
                    <p className="text-xs font-medium text-gray-600 mb-2" data-id="element-5959">
                      Complexity
                    </p>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg capitalize" data-id="element-5960">
                      {play.complexity}
                    </span>
                  </div>
                  <div data-id="element-5961">
                    <p className="text-xs font-medium text-gray-600 mb-2" data-id="element-5962">
                      AI Readiness
                    </p>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg capitalize" data-id="element-5963">
                      {play.ai_readiness}
                    </span>
                  </div>
                </div>
              </div>
              {/* Quick Stats */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200 p-6" data-id="element-5964">
                <div className="flex items-center gap-2 mb-4" data-id="element-5965">
                  <TrendingUpIcon className="w-5 h-5 text-green-600" data-id="element-5966" />
                  <h2 className="text-lg font-semibold text-green-900" data-id="element-5967">
                    Expected Impact
                  </h2>
                </div>
                <p className="text-2xl font-bold text-green-900 mb-2" data-id="element-5968">
                  {play.measurement.estimated_impact}
                </p>
                <p className="text-sm text-green-700" data-id="element-5969">
                  Based on historical performance
                </p>
              </div>
            </div>
          </div>}
        {activeTab === 'workflow' && <div className="max-w-4xl mx-auto" data-id="element-5970">
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5971">
              <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-5972">
                Play Workflow
              </h2>
              <div className="space-y-6" data-id="element-5973">
                {/* Sample workflow steps */}
                {[{
              step: 1,
              title: 'Trigger Detection',
              description: play.trigger.logic_md,
              icon: ZapIcon,
              color: 'bg-blue-100 text-blue-700'
            }, {
              step: 2,
              title: 'Account Enrichment',
              description: 'Pull firmographic data, technographic stack, and intent signals from ZoomInfo',
              icon: DatabaseIcon,
              color: 'bg-purple-100 text-purple-700'
            }, {
              step: 3,
              title: 'Multi-Channel Outreach',
              description: `Engage via ${play.channels.join(', ')} with personalized messaging`,
              icon: MailIcon,
              color: 'bg-green-100 text-green-700'
            }, {
              step: 4,
              title: 'Response Tracking',
              description: 'Monitor engagement and adjust cadence based on response',
              icon: TrendingUpIcon,
              color: 'bg-orange-100 text-orange-700'
            }].map((item, index) => {
              const Icon = item.icon;
              return <div key={item.step} data-id="element-5974">
                      <div className="flex items-start gap-4" data-id="element-5975">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-lg border-2 flex items-center justify-center ${item.color}`} data-id="element-5976">
                          <Icon className="w-5 h-5" data-id="element-5977" />
                        </div>
                        <div className="flex-1" data-id="element-5978">
                          <div className="flex items-center gap-2 mb-2" data-id="element-5979">
                            <span className="text-xs font-semibold text-gray-500" data-id="element-5980">
                              STEP {item.step}
                            </span>
                          </div>
                          <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-5981">
                            {item.title}
                          </h3>
                          <p className="text-sm text-gray-600" data-id="element-5982">
                            {item.description}
                          </p>
                        </div>
                      </div>
                      {index < 3 && <div className="ml-6 my-4 w-0.5 h-8 bg-gray-200" data-id="element-5983" />}
                    </div>;
            })}
              </div>
            </div>
          </div>}
        {activeTab === 'results' && <div className="max-w-4xl mx-auto space-y-6" data-id="element-5984">
            {/* Expected Outcomes */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5985">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5986">
                Expected Outcomes
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5987">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-5988">
                  <div className="flex items-center gap-2 mb-2" data-id="element-5989">
                    <TrendingUpIcon className="w-5 h-5 text-green-600" data-id="element-5990" />
                    <p className="text-sm font-semibold text-green-900" data-id="element-5991">
                      Primary Metric
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-green-900 mb-1" data-id="element-5992">
                    {play.measurement.estimated_impact}
                  </p>
                  <p className="text-xs text-green-700" data-id="element-5993">Expected improvement</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-5994">
                  <div className="flex items-center gap-2 mb-2" data-id="element-5995">
                    <ClockIcon className="w-5 h-5 text-blue-600" data-id="element-5996" />
                    <p className="text-sm font-semibold text-blue-900" data-id="element-5997">
                      Time to Value
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 mb-1" data-id="element-5998">
                    14 days
                  </p>
                  <p className="text-xs text-blue-700" data-id="element-5999">
                    Average time to see results
                  </p>
                </div>
              </div>
            </div>
            {/* Success Criteria */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6000">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6001">
                Success Criteria
              </h2>
              <div className="space-y-3" data-id="element-6002">
                {['Increase meeting conversion rate by 12-15%', 'Reduce time to first meeting by 30%', 'Improve response rate across all channels', 'Generate qualified pipeline within target timeframe'].map((criteria, index) => <div key={index} className="flex items-start gap-3" data-id="element-6003">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-6004" />
                    <p className="text-sm text-gray-700" data-id="element-6005">{criteria}</p>
                  </div>)}
              </div>
            </div>
            {/* Related Plays */}
            <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-6006">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-6007">
                Related Plays
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-6008">
                {mockPlaysNew.filter(p => p.id !== play.id && p.stage === play.stage).slice(0, 2).map(relatedPlay => <button key={relatedPlay.id} onClick={() => navigate(`/plays/${relatedPlay.id}`)} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors text-left" data-id="element-6009">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-6010">
                        {relatedPlay.title}
                      </h3>
                      <p className="text-xs text-gray-600 line-clamp-2" data-id="element-6011">
                        {relatedPlay.summary}
                      </p>
                    </button>)}
              </div>
            </div>
          </div>}
      </div>
    </div>;
}